import React, { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { AssessmentPriority, AssessmentResultItem } from '../../types';
import { PRIORITY_COLORS, PRIORITY_LABELS } from './AssessmentCharts';

const MAX_SCORE = 25;
const fa = (n: number) => n.toLocaleString('fa-IR');

/** مرز سه ناحیهٔ اولویت روی محور ۰ تا ۲۵ — همان آستانه‌های سرور */
const ZONES: Array<{ level: AssessmentPriority; from: number; to: number }> = [
  { level: 'HIGH', from: 0, to: 8 },
  { level: 'MEDIUM', from: 8, to: 16 },
  { level: 'LOW', from: 16, to: MAX_SCORE },
];

const TICKS = [0, 5, 10, 15, 20, 25];

interface PriorityChartProps {
  items: AssessmentResultItem[];
  onOpenCourse?: (item: AssessmentResultItem) => void;
  /** فشرده: بدون نام دوره، برای کارت‌های کوچک داشبورد */
  dense?: boolean;
}

/**
 * نمودار نقطه‌ای رتبه‌بندی‌شده (Cleveland dot plot).
 *
 * چرا این فرم: داده یک اندازه روی ۲۲ دستهٔ نام‌دار است. برای این کار،
 * نقطه روی یک محور مشترک دقیق‌ترین خوانش را می‌دهد — چشم موقعیت روی خط را
 * بهتر از طول میله یا زاویه/مساحت مقایسه می‌کند، و همهٔ برچسب‌ها هم جا می‌شوند
 * (چیزی که در فرم دایره‌ای ممکن نبود).
 *
 * محور از راست (۰) به چپ (۲۵) می‌رود تا با جهت خواندن فارسی هم‌راستا باشد؛
 * پس نقطه‌ای که راست‌تر است امتیاز کمتر و اولویت بالاتری دارد.
 */
export const PriorityChart: React.FC<PriorityChartProps> = ({ items, onOpenCourse, dense }) => {
  const [hovered, setHovered] = useState<string | null>(null);

  if (!items.length) return null;

  /** درصد از لبهٔ راست؛ چون چیدمان RTL است، ۰ در راست می‌نشیند */
  const pct = (score: number) => (score / MAX_SCORE) * 100;

  return (
    <figure className="m-0">
      {/* محور بالا — چسبان می‌ماند تا هنگام اسکرول لیست، مقیاس گم نشود */}
      <div className="sticky top-0 z-10 mb-1 bg-white/95 pb-1.5 pt-1 backdrop-blur-sm">
        <div className="flex items-end gap-3">
          <div className={dense ? 'w-24 shrink-0' : 'w-32 shrink-0 sm:w-44'} />
          <div className="relative h-4 flex-1">
            {TICKS.map((tick) => (
              <span
                key={tick}
                className="absolute -translate-x-1/2 text-[9.5px] font-black tabular-nums text-slate-400"
                style={{ right: `${pct(tick)}%`, transform: 'translateX(50%)' }}
              >
                {fa(tick)}
              </span>
            ))}
          </div>
          <div className="w-10 shrink-0" />
        </div>
        <div className="flex items-center gap-3">
          <div className={dense ? 'w-24 shrink-0' : 'w-32 shrink-0 sm:w-44'} />
          {/* نوار نواحی اولویت: خودِ محور، رنگ‌بندی‌شده */}
          <div className="relative h-1.5 flex-1 overflow-hidden rounded-full">
            {ZONES.map((zone) => (
              <span
                key={zone.level}
                className="absolute inset-y-0"
                style={{
                  right: `${pct(zone.from)}%`,
                  width: `${pct(zone.to - zone.from)}%`,
                  backgroundColor: PRIORITY_COLORS[zone.level],
                  opacity: 0.18,
                }}
              />
            ))}
          </div>
          <div className="w-10 shrink-0" />
        </div>
      </div>

      <div className="space-y-0.5">
        {items.map((item) => {
          const color = PRIORITY_COLORS[item.priority];
          const isHovered = hovered === item.sectionId;
          const clickable = !!(onOpenCourse && item.course);

          return (
            <div
              key={item.sectionId}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              onClick={() => clickable && onOpenCourse!(item)}
              onKeyDown={(e) => {
                if (clickable && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onOpenCourse!(item);
                }
              }}
              onMouseEnter={() => setHovered(item.sectionId)}
              onMouseLeave={() => setHovered(null)}
              onFocus={() => setHovered(item.sectionId)}
              onBlur={() => setHovered(null)}
              className={`flex items-center gap-3 rounded-xl px-1.5 py-1.5 transition-colors ${
                isHovered ? 'bg-slate-50' : ''
              } ${clickable ? 'cursor-pointer' : ''}`}
            >
              {/* برچسب: نام بخش + دورهٔ متصل */}
              <div className={dense ? 'w-24 shrink-0' : 'w-32 shrink-0 sm:w-44'}>
                <p className="truncate text-[11.5px] font-black text-slate-800">{item.title}</p>
                {!dense && item.course ? (
                  <p className="mt-0.5 flex items-center gap-1 truncate text-[9.5px] font-bold text-slate-400">
                    <span className="truncate">{item.course.title}</span>
                    {isHovered ? <ArrowLeft className="h-2.5 w-2.5 shrink-0" /> : null}
                  </p>
                ) : null}
              </div>

              {/* ناحیهٔ رسم: خط رابط از مبدأ تا نقطه */}
              <div className="relative h-6 flex-1">
                <span
                  aria-hidden
                  className="absolute inset-y-0 right-0 my-auto h-px w-full bg-slate-100"
                />
                <span
                  aria-hidden
                  className="absolute inset-y-0 my-auto h-[3px] rounded-full transition-all duration-500"
                  style={{
                    right: 0,
                    width: `${pct(item.score25)}%`,
                    backgroundColor: color,
                    opacity: isHovered ? 0.5 : 0.28,
                  }}
                />
                <span
                  className="absolute inset-y-0 my-auto rounded-full border-2 border-white transition-all duration-500"
                  style={{
                    right: `${pct(item.score25)}%`,
                    transform: 'translateX(50%)',
                    width: isHovered ? 15 : 12,
                    height: isHovered ? 15 : 12,
                    backgroundColor: color,
                    boxShadow: isHovered ? `0 0 0 4px ${color}22` : 'none',
                  }}
                  role="img"
                  aria-label={`${item.title}: ${fa(item.score25)} از ${fa(MAX_SCORE)} — ${PRIORITY_LABELS[item.priority]}`}
                />
              </div>

              <span
                className="w-10 shrink-0 text-left text-[12.5px] font-black tabular-nums"
                style={{ color }}
              >
                {fa(item.score25)}
              </span>
            </div>
          );
        })}
      </div>

      <figcaption className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 border-t border-slate-100 pt-2.5 text-[10px] font-black text-slate-400">
        <span>امتیاز کمتر = اولویت بالاتر</span>
        {ZONES.map((zone) => (
          <span key={zone.level} className="inline-flex items-center gap-1">
            <span
              aria-hidden
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: PRIORITY_COLORS[zone.level] }}
            />
            {PRIORITY_LABELS[zone.level]}
            <span className="text-slate-300">
              ({fa(zone.from)}–{fa(zone.to)})
            </span>
          </span>
        ))}
      </figcaption>
    </figure>
  );
};
